import React, { useEffect, useState } from "react";
import { AppButton } from "components/shared/AppButton";
import { useAuth } from "context/AuthContext";
import { BRANDS, REGIONS, VariantChange } from "utils/nipper/nipperProtocol";
import { NipperReading } from "utils/nipper/nipperSession";
import { notify } from "utils/notify";
import { AdminField, AdminGrid, Card, CardTitle } from "styles/ecosaltUpgrade.styles";

const CELL_SIZES = [15, 25, 35];

interface EcoSaltAdminVariantProps {
  reading: NipperReading | null;
  loggedIn: boolean;
  disabled?: boolean;
  onApply: (change: VariantChange) => Promise<unknown>;
}

/** Factory writes for brand, region, cell size and low salt. Admins only. */
function EcoSaltAdminVariant({ reading, loggedIn, disabled, onApply }: EcoSaltAdminVariantProps) {
  const { user } = useAuth();
  const [brand, setBrand] = useState(0);
  const [region, setRegion] = useState(0);
  const [cellSize, setCellSize] = useState(15);
  const [lowSalt, setLowSalt] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    if (!reading) return;
    setBrand(reading.variant.brandCode ?? 0);
    setRegion(reading.variant.regionCode);
    setCellSize(reading.variant.cellSize);
    setLowSalt(reading.variant.lowSalt);
  }, [reading]);

  if (user?.role !== "ADMIN" || !reading) return null;

  const busy = !loggedIn || disabled || pending !== null;

  async function apply(label: string, change: VariantChange) {
    setPending(label);
    try {
      await onApply(change);
      notify.success(`${label} updated`);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : `Could not set ${label}`);
    } finally {
      setPending(null);
    }
  }

  return (
    <Card aria-labelledby="ecosalt-admin-variant-title">
      <CardTitle id="ecosalt-admin-variant-title">Set variant</CardTitle>
      <AdminGrid>
        <AdminField>
          Brand
          <span className="controls">
            <select aria-label="Brand" value={brand} disabled={busy} onChange={(e) => setBrand(Number(e.target.value))}>
              {BRANDS.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
            <AppButton aria-label="Set Brand" variant="ghost" disabled={busy} onClick={() => void apply("Brand", { brandCode: brand })}>
              Set
            </AppButton>
          </span>
        </AdminField>
        <AdminField>
          Region
          <span className="controls">
            <select aria-label="Region" value={region} disabled={busy} onChange={(e) => setRegion(Number(e.target.value))}>
              {REGIONS.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
            <AppButton aria-label="Set Region" variant="ghost" disabled={busy} onClick={() => void apply("Region", { regionCode: region })}>
              Set
            </AppButton>
          </span>
        </AdminField>
        <AdminField>
          Cell size
          <span className="controls">
            <select aria-label="Cell size" value={cellSize} disabled={busy} onChange={(e) => setCellSize(Number(e.target.value))}>
              {CELL_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <AppButton aria-label="Set Cell Size" variant="ghost" disabled={busy} onClick={() => void apply("Cell size", { cellSize })}>
              Set
            </AppButton>
          </span>
        </AdminField>
        <AdminField>
          Low salt
          <span className="controls">
            <select
              aria-label="Low salt"
              value={lowSalt ? "yes" : "no"}
              disabled={busy}
              onChange={(e) => setLowSalt(e.target.value === "yes")}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
            <AppButton aria-label="Set Low Salt" variant="ghost" disabled={busy} onClick={() => void apply("Low salt", { lowSalt })}>
              Set
            </AppButton>
          </span>
        </AdminField>
      </AdminGrid>
    </Card>
  );
}

export default EcoSaltAdminVariant;
